import { Checkbox } from '@fluentui/react';
import { StackShim as Stack, StackItemShim as StackItem } from '@fluentui/react-migration-v8-v9';
import {
  Button,
  FluentProvider,
  webLightTheme,
  Field,
  Input,
  Select,
  Label,
  Tooltip,
  makeStyles,
} from '@fluentui/react-components';
import { InfoRegular } from '@fluentui/react-icons';
import React, { Dispatch, SetStateAction, useContext, useEffect, useState } from 'react';
import { Controller, DeepPartial, useForm, useWatch } from 'react-hook-form';
import hasAccess from '../../../../utils/authorization/authorizationCheck';
import UserContext from '../../../../utils/authorization/UserContext';
import { StatUser } from '../../../../utils/statApi/UsersApi';
import IProcessConfig from '../../../../utils/types/IProcessConfig';
import FeatureFlagged from '../../../common/featureFlagged/featureFlagged';
import UtcDatePicker from '../../../common/utcDatePicker/UtcDatePicker';
import ComplianceRequirementPicker from '../../../countries/ComplianceRequirementPicker';
import { ProcessType } from '../../processes/Interfaces';
import { filingDueDateMinDate, reportingPeriodEndDateMaxDate } from './CreateProcessConfigUtils';
import { iconButtonStyles } from '../../../../app/common/styles/CommonStyleObjects';

interface IEditProcessConfigDetailsFormProps {
  onValidated: (isValid: boolean) => void;
  setData: Dispatch<SetStateAction<IProcessConfig>>;
  data: IProcessConfig;
}

export const offsetDateIconStyles = {
  marginLeft: 5,
};

const useStyles = makeStyles({
  positionRelative: { position: 'relative' },
});

const EditProcessConfigDetailsForm = (props: IEditProcessConfigDetailsFormProps): JSX.Element => {
  const { data, setData, onValidated } = props;
  const classes = useStyles();
  const [isAnnualFinancialStatement, setIsAnnualFinancialStatement] = useState(false);
  const disableUseLastDayOfMonth = data.recurrence === 'ONETIME' || data.recurrence === 'WEEKLY';
  const user: StatUser = useContext(UserContext);
  const form = useForm<IProcessConfig>({
    reValidateMode: 'onChange',
    mode: 'onChange',
    defaultValues: data as IProcessConfig,
  });

  const { errors, setValue, control, formState, getValues, trigger } = form;
  const { isValid, dirtyFields } = formState;

  const watchForm: DeepPartial<IProcessConfig> = useWatch({
    control,
    defaultValue: data as IProcessConfig,
  });

  useEffect(() => {
    onValidated(isValid);
    setIsAnnualFinancialStatement(
      data?.processType?.fieldName === ProcessType.AnnualFinancialStatements,
    );
    if (isValid) {
      setData({
        ...data,
        name: getValues('name'),
        filingDueDate: getValues('filingDueDate'),
        reportingPeriodEndDate: getValues('reportingPeriodEndDate'),
        useLastDayOfMonth: getValues('useLastDayOfMonth'),
      });
    }
  }, [isValid, watchForm]);

  const getMinDate = (utcDateString: Date): Date => {
    if (utcDateString == null) {
      return null;
    }

    const utcDate = new Date(utcDateString);
    const localizedDate = new Date(
      utcDate.getUTCFullYear(),
      utcDate.getUTCMonth(),
      utcDate.getUTCDate() + 1,
    );

    return localizedDate;
  };

  const getMinFilingDueDate = (): Date => {
    const minProcessDate = getMinDate(data?.minFilingDueDate);
    const minFilingDueDate = filingDueDateMinDate(data?.reportingPeriodEndDate);
    const latestMinDate = minProcessDate > minFilingDueDate ? minProcessDate : minFilingDueDate;
    return latestMinDate;
  };

  const { permissions } = user;
  const permission = isAnnualFinancialStatement
    ? 'ProcessConfiguration:EditReportingPeriodEndDateAFS'
    : 'ProcessConfiguration:EditReportingPeriodEndDate';
  const preventEditReportingPeriodEndDate = !hasAccess(permissions, permission);
  const preventEditFilingDueDate =
    isAnnualFinancialStatement &&
    !hasAccess(permissions, 'ProcessConfiguration:EditFilingDueDateAFS');
  return (
    <FluentProvider theme={webLightTheme}>
      <Stack tokens={{ childrenGap: 10 }}>
        <FeatureFlagged flagName="CountryConfiguration">
          <Stack horizontal>
            <Controller
              name="complianceRequirement"
              control={control}
              disabled
              defaultValue={[data?.complianceRequirement]}
              render={(): React.ReactElement => (
                <ComplianceRequirementPicker
                  selectedItem={
                    data?.complianceRequirement
                      ? [
                          {
                            key: data?.complianceRequirement?.id,
                            name: data?.complianceRequirement?.name,
                          },
                        ]
                      : []
                  }
                  setSelectedItem={null}
                  isDisabled
                />
              )}
            />
          </Stack>
        </FeatureFlagged>

        <Label id="processTypeSelect">Process Type</Label>
        <Select
          aria-labelledby="processTypeSelect"
          disabled
          aria-disabled
          defaultValue={data?.processType?.fieldName}
        >
          <option key={data?.processType?.fieldName} value={data?.processType?.fieldName}>
            {data?.processType?.fieldName}
          </option>
        </Select>

        <Label id="processTemplateSelect">Process Template</Label>
        <Select
          aria-labelledby="processTemplateSelect"
          disabled
          aria-disabled
          defaultValue={data?.processTemplate?.fieldName}
        >
          <option key={data?.processTemplate?.fieldName} value={data?.processTemplate?.fieldName}>
            {data?.processTemplate?.fieldName}
          </option>
        </Select>

        <Controller
          name="name"
          control={control}
          rules={{
            required: 'Name is required',
            maxLength: {
              value: 200,
              message: 'Input is greater than the maximum character limit of 200.',
            },
          }}
          render={({ onChange, onBlur, value, name, ref }) => (
            <Field
              label="Name"
              required
              validationMessage={dirtyFields?.name && errors?.name?.message}
            >
              <Input
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                name={name}
                ref={ref}
                id="name"
              />
            </Field>
          )}
        />
        <Stack horizontal tokens={{ childrenGap: 10 }}>
          <StackItem grow className={classes.positionRelative}>
            <Controller
              name="reportingPeriodEndDate"
              control={control}
              defaultValue={data?.reportingPeriodEndDate}
              render={({ value }): React.ReactElement => (
                <>
                  <UtcDatePicker
                    label="Next Reporting Period End Date"
                    isRequired
                    value={value}
                    minDate={getMinDate(data?.minReportingPeriodEndDate)}
                    maxDate={reportingPeriodEndDateMaxDate(data?.filingDueDate)}
                    onSelectDate={(utcDateString: string): void => {
                      setValue('reportingPeriodEndDate', utcDateString, { shouldDirty: true });
                      trigger();
                    }}
                    disabled={preventEditReportingPeriodEndDate}
                  />
                  {preventEditReportingPeriodEndDate && (
                    <Tooltip
                      withArrow
                      content={
                        <span>You do not have access to change the reporting period end date</span>
                      }
                      relationship="label"
                    >
                      <FluentProvider theme={webLightTheme}>
                        <Button
                          appearance="subtle"
                          aria-label="You do not have access to change the reporting period end date"
                          icon={<InfoRegular color={iconButtonStyles.color} />}
                          style={offsetDateIconStyles}
                        />
                      </FluentProvider>
                    </Tooltip>
                  )}
                </>
              )}
              errorMessage={
                dirtyFields?.reportingPeriodEndDate && errors?.reportingPeriodEndDate?.message
              }
              rules={{
                required: 'Please provide a reporting period due date',
              }}
            />
          </StackItem>
          <StackItem grow className={classes.positionRelative}>
            <Controller
              name="filingDueDate"
              control={control}
              defaultValue={data?.filingDueDate}
              render={({ value }): React.ReactElement => (
                <>
                  <UtcDatePicker
                    label="Next Filing Due Date"
                    isRequired
                    value={value}
                    minDate={getMinFilingDueDate()}
                    onSelectDate={(utcDateString: string): void => {
                      setValue('filingDueDate', utcDateString, { shouldDirty: true });
                      trigger();
                    }}
                    disabled={preventEditFilingDueDate}
                  />

                  {preventEditFilingDueDate && (
                    <Tooltip
                      withArrow
                      content={
                        <>
                          Only admins and STAT Info users can change the filing due date for an AFS
                          process
                        </>
                      }
                      relationship="label"
                    >
                      <FluentProvider theme={webLightTheme}>
                        <Button
                          appearance="subtle"
                          aria-label="Only admins and STAT Info users can change the filing due date for an AFS process"
                          icon={<InfoRegular color={iconButtonStyles.color} />}
                          style={offsetDateIconStyles}
                        />
                      </FluentProvider>
                    </Tooltip>
                  )}
                </>
              )}
              errorMessage={dirtyFields?.filingDueDate && errors?.filingDueDate?.message}
              rules={{
                required: 'Please provide a filing due date',
              }}
            />
          </StackItem>
        </Stack>
        <Controller
          name="useLastDayOfMonth"
          control={control}
          defaultValue={data?.useLastDayOfMonth}
          render={({ onBlur, value, name }): React.ReactElement => (
            <Checkbox
              label="Use Last Day of Month"
              onChange={(
                e: React.FormEvent<HTMLInputElement | HTMLElement>,
                checked: boolean,
              ): void => {
                setValue('useLastDayOfMonth', checked, { shouldDirty: true });
                trigger();
              }}
              checked={value}
              name={name}
              disabled={disableUseLastDayOfMonth}
            />
          )}
        />

        <Label id="recurrenceSelect">Recurrence</Label>
        <Select
          aria-labelledby="recurrenceSelect"
          disabled
          aria-disabled
          defaultValue={data?.recurrence}
        >
          <option key={data?.recurrence} value={data?.recurrence}>
            {data?.recurrence}
          </option>
        </Select>
      </Stack>
    </FluentProvider>
  );
};
export default EditProcessConfigDetailsForm;
